/** @jsx jsx */

import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  Fragment as _,
} from 'react';
import { css as _css } from 'emotion';
import { css, jsx } from '@emotion/core';
import { colors, getButtonFocus } from 'styles/variables';
import getBlockById from 'services/apiHelpers/getBlockById';
import { interactionContext } from './interactionContext';
import { useSelector } from 'react-redux';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import hexToRGB from 'services/hexToRGB';
import getCurrentTopic from 'services/apiHelpers/getCurrentTopic';
import pubSub from 'services/pubSub';
import TopicIcon from './TopicIcon';
import getTopicSId from 'services/getTopicSId';
import { getTopicsComplete } from 'services/apiHelpers/getTopicsComplete';
import getTopics from 'services/apiHelpers/getTopics';
import hexToRgb, { getHexToRgbWithDefaultFallback } from 'services/hexToRGB';

const { lGrey0, lPink } = colors;

const ProgressBar = ({ color, percentComplete }) => {
  const width = 130;
  const height = 8;
  const { r, g, b } = hexToRGB(color);

  return (
    <div
      css={css`
        position: relative;
        width: ${width}px;
        height: ${height}px;
        border-radius: 9999px;
        background: rgba(${r}, ${g}, ${b}, 0.3);
        transition: background 0.1s;
      `}
    >
      <div
        css={css`
          position: absolute;
          left: 0;
          top: 0;
          width: ${width * (percentComplete / 100)}px;
          height: ${height}px;
          border-radius: 9999px;
          background: rgb(${r}, ${g}, ${b});
          transition: background 0.1s;
        `}
      />
    </div>
  );
};

const TopicNameContainer = ({ addImage, cardSize, children }) => {
  const shared = `
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  left: 0;
  width: 100%;
  `;
  return !!addImage ? (
    <div
      css={css`
        ${shared}
        top: ${cardSize === 'small' ? '40px' : '55px'};
        height: calc(100% - ${cardSize === 'small' ? '95px' : '110px'});
      `}
    >
      {children}
    </div>
  ) : (
    <div
      css={css`
        ${shared}
        top: 20px;
        height: calc(100% - 75px);
      `}
    >
      {children}
    </div>
  );
};

const TopicPanel = ({
  topic,
  updateTopicComplete,
  displayedTopics,
  children,
}) => {
  const context: any = useContext(interactionContext);
  const { id: interactionId } = context;
  const {
    graphColor = '#61cece',
    cardColor = '#fff',
    textColor = '#1e2533',
    navigateToTopic = true,
    addImage = true,
    // iconSize,
    cardWidth = 235,
    cardHeight = 150,
    hideTopicsThatIncludeATopicPortal = true,
    completeInSequence = false,
    includeShadow = true,
    focusColor = '#000000',
    panelBackgroundOpacity = 100,
    panelOutline = false,
    panelOutlineColor,
    borderRadius = 20,
    iconWidth,
    iconHeight,
    backgroundMedia = false,
    includeCustomText = false,
    positionTextBelow = false,
    fontSize = 16,
  } = context.props;

  const [media, txt] = children || [];

  let imageFit;
  if (media?.props?.child === 1) {
    imageFit = media?.props?.children[1]?.props?.fit;
  }

  let { font = '' } = context.props;

  const { r, g, b } = getHexToRgbWithDefaultFallback(cardColor, '#ffffff');

  let a = panelBackgroundOpacity / 100;
  if (!cardColor) {
    a = 0;
  }

  const { focusedNodeId, theme, interactionsSeen } = useSelector(
    (state: any) => state
  );
  const interactionsInTopics = useMemo(
    () =>
      getTopics().reduce((acc, curr, index) => {
        let topicName = curr?.props?.name;
        //
        return {
          ...acc,
          [curr.id]: getInteractionsInTopic(curr),
        };
      }, {}),
    []
  );
  const [iconSelected, setIconSelected] = useState<Boolean>(false);
  const { id } = topic;
  const { name, topicIcon = {} } = topic.props;
  const isEditing = focusedNodeId === interactionId;

  const cardSize = cardWidth === 235 ? 'small' : 'large';

  let { iconSize } = context.props;
  if (!iconSize) {
    iconSize = cardWidth <= 235 ? 60 : 90;
  }

  let navigationEnabled =
    !isEditing && navigateToTopic && getCurrentTopic()!.id !== id;

  const navigate = navigationEnabled
    ? () => {
        if (window.dev && window.dev()) {
          return;
        }

        const targetLocation = getTopicSId(topic);

        if (targetLocation) {
          window.location.hash = targetLocation;
        }
      }
    : null;

  // nuclear
  if (window.dev()) {
    navigationEnabled = false;
  }

  const interactionsInTopic = getInteractionsInTopic(topic)
    .filter((i: any) => {
      if (!hideTopicsThatIncludeATopicPortal) {
        return true;
      }
      return i?.meta?.subType !== 'TopicPortal';
    })
    .map((i: any) => i.id);

  const interactionsInTopicSeen = interactionsInTopic.filter((i: any) => {
    return interactionsSeen
      .filter((i: any) => {
        return i && getBlockById(i)?.meta?.subType !== 'TopicPortal';
      })
      .includes(i);
  });

  const topicsComplete = getTopicsComplete(
    interactionsInTopics,
    interactionsSeen
  );

  let percentComplete = Number(
    (
      (interactionsInTopicSeen.length / interactionsInTopic.length) *
      100
    ).toFixed()
  );

  if (isNaN(percentComplete)) {
    percentComplete = 0;
  }

  // update font : theme > local
  // does the section get a font ?
  font = !font ? theme?.fontFamily : font;
  //

  let unlocked;
  const topics = useMemo(() => {
    return displayedTopics || getTopics();
  }, []);
  if (!!completeInSequence) {
    topics.forEach((t, i, arr) => {
      if (i === 0) {
        unlocked = true;
      } else if (t.id === id) {
        unlocked = topicsComplete[arr[i - 1]?.id];
      }
    });
  } else {
    unlocked = true;
  }

  if (window.dev()) {
    unlocked = true;
  }

  useEffect(() => {
    const pS = pubSub.subscribe('UPDATE_TOPIC_ICON', $ => {
      if (!$.topic) {
        return;
      }
      $.topic.id !== id && setIconSelected(false);
    });

    return () => pS();
  }, []);

  useEffect(() => {
    iconSelected &&
      pubSub.publish(
        'UPDATE_TOPIC_ICON',
        iconSelected
          ? {
              topic,
            }
          : null
      );
  }, [iconSelected]);

  useEffect(() => {
    if (percentComplete === 100) {
      updateTopicComplete(topic.id);
    }
  }, [percentComplete]);

  let focus;
  // if(!focusColor) {}
  focus = getButtonFocus(
    (focusColor || cardColor) !== '#fff' ? focusColor || cardColor : '#1e2533',
    '0 20px 60px -25px rgba(0, 0, 0, 0.4)'
  ).split(':')[1];

  const pointerEvents = unlocked ? 'auto' : 'none';

  const title = `Go to ${name}. Progress: ${percentComplete}%`;

  return interactionsInTopic.length === 0 ? null : (
    <div
      css={css({
        position: 'relative',

        width: cardWidth,
        margin: `${addImage ? iconSize / 2 : 20}px 15px 20px 15px`,
        //

        pointerEvents,
        // and ANYTHING in-between
        '*': {
          pointerEvents,
        },

        opacity: unlocked ? 1 : 0.2,

        '.chameleon-image': {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: cardWidth,
          height: cardHeight,
        },

        '.chameleon-image img': {
          width: `${cardWidth -
            (imageFit === 'contain' ? 20 : 0)}px !important`,
          height: `${cardHeight -
            (imageFit === 'contain' ? 20 : 0)}px !important`,
        },
      })}
    >
      <a
        href={`#${getTopicSId(topic)}`}
        title={title}
        target="_self"
        css={css({
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',

          width: 'inherit',
          height: cardHeight,

          ...(panelOutline && {
            border: `1px solid ${panelOutlineColor || 'rgba(0, 0, 0, 0.05)'}`,
          }),

          borderRadius,
          background: `rgba(${r}, ${g}, ${b}, ${a})}`,

          boxShadow: includeShadow
            ? `0 20px 60px -25px rgba(0, 0, 0, 0.4)`
            : 'none',

          transition: 'all 0.15s',

          ...(navigationEnabled
            ? {
                cursor: 'pointer',

                '&:hover': {
                  opacity: 0.8,
                },
              }
            : { pointerEvents: 'none' }),

          '&:focus': {
            boxShadow: focus,
          },

          overflow: 'hidden',
        })}
        onClick={e => {
          if (window.dev()) {
            e.preventDefault();
            return;
          }
        }}
        onKeyUp={e => {
          if (![' ', 'Enter'].includes(e.key)) {
            return;
          }

          if (window) {
            window.location.hash = getTopicSId(topic) as string;
          }
        }}
      >
        {!!backgroundMedia && (
          <div
            css={css({
              width: cardWidth,
              height: cardHeight,

              // pointerEvents: 'auto', < this was overriding the root pointerEvent
              '.chameleon-image > div': {
                borderRadius,
              },
            })}
          >
            {media}
          </div>
        )}

        {!positionTextBelow && (
          <_>
            {!includeCustomText ? (
              <TopicNameContainer addImage={addImage} cardSize={cardSize}>
                <div
                  css={css`
                    padding: 5px 15px;
                    font-size: ${fontSize}px;
                    font-family: ${font};
                    color: ${textColor};
                    text-align: center;
                  `}
                >
                  {name}
                </div>
              </TopicNameContainer>
            ) : (
              <div
                css={css({
                  position: 'absolute',
                  left: 0,
                  width: '100%',
                  padding: window.dev() ? 5 : 0,
                  // zIndex: 1, // causing overlap with header in output when scrolling
                  pointerEvents: 'auto',
                })}
              >
                {txt}
              </div>
            )}

            <div
              css={css`
                position: absolute;
                display: flex;
                flex-direction: column;
                align-items: center;
                left: 0;
                bottom: 0;
                width: 100%;
                height: ${cardSize === 'small' ? '55px' : '65px'};
              `}
            >
              <div
                css={css({
                  margin: '0 0 0px 0',
                  fontSize: '14px',
                  color: graphColor,
                })}
              >
                {percentComplete}%
              </div>
              <ProgressBar
                color={graphColor}
                percentComplete={percentComplete}
              />
            </div>
          </_>
        )}
      </a>

      {!!positionTextBelow && (
        <div
          css={css({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: window.dev() ? 0 : 10,
          })}
        >
          <div
            css={css`
              width: 100%;
              padding: 5px 15px;
              font-size: ${fontSize}px;
              font-family: ${font};
              color: ${textColor};
              text-align: center;
            `}
          >
            {!includeCustomText ? name : txt}
          </div>
          <div
            css={css({
              margin: '0 0 0px 0',
              fontSize: '14px',
              color: graphColor,
            })}
          >
            {percentComplete}%
          </div>
          <ProgressBar color={graphColor} percentComplete={percentComplete} />
        </div>
      )}

      {!!addImage && (
        <TopicIcon
          topicIcon={topicIcon}
          size={iconSize}
          // || cardWidth <= 235 ? 60 : 90}
          isEditing={isEditing}
          iconSelected={iconSelected}
          setIconSelected={setIconSelected}
        />
      )}
    </div>
  );
};

export default TopicPanel;
